import { fetchPostSeller } from '@iwoca/lapi-client/edge';

import styles from './AccountActivation.module.css';
import { BookDemo } from './BookDemo/BookDemo';
import { ReferralDisclaimer } from './components/ReferralDisclaimer/ReferralDisclaimer';
import { DirectorsProfile } from './DirectorsProfile/DirectorsProfile';
import { REFERRAL_CODE_STORAGE_KEY } from './hooks/useReferralCode';
import { postDirectorsProfileToHubspot } from './hubspotApi';
import { PendingCra } from './PendingCra/PendingCra';
import { Policy } from './Policy/Policy';
import { SecureYourPayments } from './SecureYourPayments/SecureYourPayments';
import { SignAgreement } from './SignAgreement/SignAgreement';
import { SteppedForm } from './SteppedForm/SteppedForm';
import { YourBusiness } from './YourBusiness/YourBusiness';
import GreenCheckmarkSVG from '../../../src/components/svg/GreenCheckmark.svg';
import { useActivationSteps } from '../../activation/useActivationSteps.hook';
import {
  useGetSellerByStateKey,
  useGetStateByStateKey,
} from '../../api/lending/lapiHooks';
import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen';
import { useSafeNavigate } from '../../hooks/useSafeNavigate';
import { useStateKey } from '../../hooks/useStateKey.hook';

export const AccountActivation = () => {
  const { stateKey } = useStateKey();
  const { state: customerState } = useGetStateByStateKey();
  const { currentStep, completedSteps, isOnboarded, isLoading } =
    useActivationSteps();

  const createSeller = useCreateSeller();

  const { navigate } = useSafeNavigate();

  if (isOnboarded) {
    navigate({ to: '/pay/dashboard/', keepParams: true });
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className={styles.contentContainer}>
      <div className={styles.leftSideContent}>
        <h1 className={styles.activationTitle}>Let's get you set up</h1>
        <p className={styles.activationDescription}>
          Just fill in some basic information - you won’t need anything to hand
          other than the bank details for the account you’d like to get paid
          into.
        </p>
        <ReferralDisclaimer />
        <div className={styles.activationFormBody}>
          <SteppedForm
            open={currentStep === 'directorsProfile'}
            head={
              <SteppedFormHead
                number="1"
                description="Director's profile"
                icon={getIcon(completedSteps?.directorsProfile)}
              />
            }
          >
            <DirectorsProfile onSubmit={createSeller} />
          </SteppedForm>
          <SteppedForm
            open={currentStep === 'businessProfile'}
            head={
              <SteppedFormHead
                number="2"
                description="Your business"
                icon={getIcon(completedSteps?.businessProfile)}
              />
            }
          >
            {stateKey && <YourBusiness />}
          </SteppedForm>
          <SteppedForm
            open={
              currentStep === 'bankDetails' || currentStep === 'identityCheck'
            }
            head={
              <SteppedFormHead
                number="3"
                description="Secure your payments"
                icon={getIcon(completedSteps?.bankDetails)}
              />
            }
            canOpen={
              !!completedSteps?.directorsProfile && !completedSteps?.bankDetails
            }
          >
            {customerState && <SecureYourPayments isLoading={isLoading} />}
          </SteppedForm>
          {stateKey && currentStep === 'riskAssessment' && <PendingCra />}
          {stateKey && currentStep === 'contract' && <SignAgreement />}
        </div>
        <NeedSomeHelp />
      </div>
      <div className={styles.rightSideContent}>
        <BookDemo />
        <Policy />
      </div>
    </div>
  );
};

const NeedSomeHelp = () => {
  return (
    <div className={styles.needHelp}>
      <h3 className={styles.needHelpTitle}>Need some help?</h3>
      <p className={styles.giveUsACall}>
        Give us a call on{' '}
        <a href="tel:02037780549" className={styles.needHelpNumber}>
          020 3778 0549
        </a>
        <p className={styles.openingHours}>Mon - Fri (9am - 6pm)</p>
      </p>
    </div>
  );
};

const SteppedFormHead = ({
  number,
  description,
  icon,
}: {
  number: string;
  description: string;
  icon?: React.ReactNode;
}) => {
  return (
    <div className={styles.headContainer}>
      <div className={styles.headTitle}>
        {icon} {number}.
        <span className={styles.description}>{description}</span>
      </div>
    </div>
  );
};

function getIcon(isCompleted?: boolean) {
  if (Boolean(isCompleted)) {
    return (
      <img
        src={GreenCheckmarkSVG}
        className={styles.greenCheckmark}
        alt=""
        width="20"
        height="20"
      />
    );
  } else {
    return <></>;
  }
}

function useCreateSeller() {
  const { stateKey } = useStateKey();
  const { state } = useGetStateByStateKey();
  const { seller } = useGetSellerByStateKey();

  const createSeller = async () => {
    if (!stateKey || !state) {
      throw new Error('No customer state found');
    }

    if (seller) return;

    const referral_code = sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY);

    const sellerBody = referral_code ? { data: { referral_code } } : {};
    await fetchPostSeller({ stateKey, body: sellerBody });

    await postDirectorsProfileToHubspot(state);
  };

  return createSeller;
}
